import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Trans, useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  LayoutDashboard,
  Users,
  Save,
  FolderOpen,
  BookOpen,
  ChevronRight,
  Tag,
  Lock,
  Calculator,
  CheckCircle,
} from "lucide-react";

interface SectionProps {
  icon: React.ReactNode;
  step?: string;
  title: string;
  children: React.ReactNode;
}

function Section({ icon, step, title, children }: SectionProps) {
  return (
    <div className="border border-border rounded-xl p-6 bg-card">
      <div className="flex items-start gap-4 mb-4">
        <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center shrink-0 text-primary">
          {icon}
        </div>
        <div>
          {step && <p className="text-xs font-semibold uppercase tracking-wider text-primary mb-0.5">{step}</p>}
          <h2 className="text-lg font-semibold text-foreground">{title}</h2>
        </div>
      </div>
      <div className="text-sm text-muted-foreground space-y-3 leading-relaxed">
        {children}
      </div>
    </div>
  );
}

function Field({ name, desc }: { name: string; desc: string }) {
  return (
    <div className="flex gap-2">
      <ChevronRight className="w-4 h-4 text-primary shrink-0 mt-0.5" />
      <span><span className="font-medium text-foreground">{name}:</span> {desc}</span>
    </div>
  );
}

/**
 * Pricing Manager — tutorial (port de tutorial.tsx de Replit; el header
 * propio murió, lo da la Layout de la plataforma).
 */
export default function PricingTutorialPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="min-h-full flex flex-col bg-background">
      <div className="bg-muted border-b px-6 py-3 flex items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/finance/pricing")}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
          data-testid="button-back-home"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('pricing.tutorial.back_to_saved_versions')}
        </Button>
        <span className="text-muted-foreground/40">|</span>
        <div className="flex items-center gap-2 text-muted-foreground">
          <BookOpen className="w-4 h-4" />
          <span className="text-sm font-medium">{t('pricing.tutorial.how_it_works')}</span>
        </div>
      </div>

      <main className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-6 py-10 space-y-6">

          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-foreground mb-2">{t('pricing.tutorial.how_to_use_the_pricing_manager')}</h1>
            <p className="text-muted-foreground">{t('pricing.tutorial.a_step_by_step_guide_for')}</p>
          </div>

          {/* Overview */}
          <Section icon={<LayoutDashboard className="w-5 h-5" />} title="Overview">
            <p>
              <Trans i18nKey="pricing.tutorial.overview_intro" components={{ b: <span className="font-medium text-foreground" /> }} />
            </p>
            <p>
              <Trans i18nKey="pricing.tutorial.overview_versions" components={{ b: <span className="font-medium text-foreground" /> }} />
            </p>
            <p><Trans i18nKey="pricing.tutorial.overview_sidebar" components={{ b: <span className="font-medium text-foreground" /> }} /></p>
          </Section>

          {/* Step 1: Deal Context */}
          <Section icon={<Tag className="w-5 h-5" />} step="Step 1" title={t('pricing.tutorial.deal_context')}>
            <p>
              <Trans i18nKey="pricing.tutorial.deal_context_intro" components={{ b: <span className="font-medium text-foreground" /> }} />
            </p>
            <div className="space-y-2 mt-3">
              <Field name="Client" desc="Searchable dropdown — type to filter. Select the client this proposal is for." />
              <Field name="Deal Name" desc="A short internal name for this version (max 5 words). Shows in the header and Saved Versions list." />
              <Field name="Segment" desc="Auto-populated based on the client selected from the sheet." />
              <Field name="Currency" desc="The billing currency for this deal (e.g. US$, AR$)." />
              <Field name="Society" desc="The legal entity issuing this proposal (e.g. LLC (US), SA (AR))." />
              <Field name="Revenue Type" desc="Project-Based or Retainer — describes the contract structure." />
              <Field name="Deal Type" desc="Single Deal allows one solution; Bundle Deal allows multiple solutions." />
              <Field name="Solutions" desc="The Winclap services included (Owned Media Lab, Paid Media Lab, Content, etc.). Content and Paid Media Lab each get their own dedicated tab." />
              <Field name="Duration (months)" desc="How many months the deal runs — between 1 and 60. Used in all cost calculations." />
              <Field name="Payment Terms" desc="0, 30, 60, 90, or 120 days. Longer terms add a financing cost multiplier to total costs." />
              <Field name="Comments" desc="Free-text field for any notes visible on the saved version." />
            </div>
          </Section>

          {/* Step 2: Team & Assets */}
          <Section icon={<Users className="w-5 h-5" />} step="Step 2" title={t('pricing.tutorial.team_assets')}>
            <p>
              {t('pricing.tutorial.depending_on_which_solutions_you_selected')}
            </p>
            <div className="space-y-2 mt-3">
              <Field
                name="Regular tab"
                desc="For solutions other than Paid Media Lab and Content. Add team members by role and country, assets by type and seniority, and any other fixed monthly costs. The tool looks up costs from the pricing sheet automatically."
              />
              <Field
                name="Team & Assets (PML)"
                desc="Appears when Paid Media Lab is selected. Define campaigns (name, type, media, quantity), set the Sourcing Team allocation per role, and view the Suggested Team calculated on Recalculate."
              />
              <Field
                name="Team & Assets (Content)"
                desc="Appears when Content is selected. Set Client Geos and Complexity variables, add asset rows (video, static, playable, etc.), and fill in the team with countries per role. If you add Creator Video or Advocates assets, a detail table appears to distribute quantities by country and follower tier."
              />
            </div>
            <p className="mt-3">
              <span className="font-medium text-foreground">{t('pricing.tutorial.other_costs')}</span> {t('pricing.tutorial.visible_in_the_regular_tab_lets')}
            </p>
            <div className="flex items-start gap-2 mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <Calculator className="w-4 h-4 text-yellow-400 shrink-0 mt-0.5" />
              <p className="text-black font-bold">
                <Trans i18nKey="pricing.tutorial.recalculate_important" components={{ b: <span /> }} />
              </p>
            </div>
          </Section>

          {/* Step 3: Margins */}
          <Section icon={<Calculator className="w-5 h-5" />} step="Step 3" title={t('pricing.tutorial.margins_metrics_panel')}>
            <p>
              <Trans i18nKey="pricing.tutorial.metrics_panel_intro" components={{ b: <span className="font-medium text-foreground" /> }} />
            </p>
            <div className="space-y-2 mt-3">
              <Field
                name="Desired Margin"
                desc="Enter a target profit margin (%) per solution. The tool calculates the Suggested Pricing automatically."
              />
              <Field
                name="Desired Pricing"
                desc="Enter the price you want to charge. The tool shows the Obtained Margin automatically."
              />
              <Field
                name="Recalculate button"
                desc="Turns yellow whenever team/asset data changes. Click it to refresh all cost totals and pricing suggestions. The button turns gray once applied."
              />
              <Field
                name="Bundle Pricing"
                desc="Appears automatically when more than one solution is selected. Shows the combined total across all solutions — both for Suggested and Desired approaches."
              />
            </div>
            <p className="mt-3">
              <span className="font-medium text-foreground">Tip:</span> {t('pricing.tutorial.changing_any_team_row_asset_row')}
            </p>
          </Section>

          {/* Saving & Approvals */}
          <Section icon={<Save className="w-5 h-5" />} title={t('pricing.tutorial.saving_approvals')}>
            <p>{t('pricing.tutorial.there_are_two_distinct_actions_for')}</p>
            <div className="space-y-2 mt-3">
              <Field
                name="Save Changes (header button)"
                desc="Saves all current data and keeps the version fully editable. Use this to preserve progress at any point. Note: saving clears the previously generated report link — you'll need to regenerate it from Saved Versions."
              />
              <Field
                name="Send for Approval (always yellow button)"
                desc="Opens a dialog asking whether to share Desired Margins or Desired Pricing with approvers. On Send: the current data is saved, the version is permanently locked, an email with the full report is sent to all recipients in the BBDD_Emails list (column D = Yes), and the approval status is set."
              />
            </div>
            <div className="flex items-start gap-2 mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <CheckCircle className="w-4 h-4 text-yellow-400 shrink-0 mt-0.5" />
              <p>
                <Trans i18nKey="pricing.tutorial.pre_approved" components={{ b: <span className="font-medium text-foreground" /> }} />
              </p>
            </div>
            <div className="flex items-start gap-2 mt-4 p-3 bg-muted rounded-lg">
              <Lock className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
              <p>
                <Trans i18nKey="pricing.tutorial.locked_versions_p" components={{ b: <span className="font-medium text-foreground" /> }} />
              </p>
            </div>
          </Section>

          {/* Saved Versions */}
          <Section icon={<FolderOpen className="w-5 h-5" />} title={t('pricing.tutorial.saved_versions')}>
            <p>
              <Trans i18nKey="pricing.tutorial.saved_versions_p" components={{ b: <span className="font-medium text-foreground" /> }} />
            </p>
            <div className="space-y-2 mt-3">
              <Field name="Version Link" desc="Opens the full pricing — editable if not locked, read-only if locked." />
              <Field
                name="Report"
                desc="Generates a full internal Google Sheets report with all cost breakdowns. Shows a Generate button if none exists yet, or an Open link if already generated. The report is cleared whenever you Save Changes, so regenerate after any updates."
              />
              <Field
                name="Duplicate"
                desc="Creates an editable copy of the version with all data pre-filled (except the Deal Name). Useful for creating variations or updating a locked version."
              />
              <Field name="Status" desc="Shows Editable (yellow) or Locked (gray) based on whether the version has been sent for approval." />
              <Field
                name="Approved"
                desc="Shows the current approval status: Pending (grey), Pre-Approved (green), Approved (green), or Denied (red). Pre-Approved is set automatically when conditions are met. Approvers can set Approved or Denied on Pending versions — once set, the status is permanent and cannot be changed."
              />
              <Field name="Delete" desc="Permanently removes the version after confirmation." />
            </div>
          </Section>

          <div className="flex justify-center pt-4">
            <Button
              onClick={() => navigate("/finance/pricing/new")}
              size="lg"
              className="w-64"
              data-testid="button-go-to-home"
            >
              {t('pricing.tutorial.start_creating_a_pricing')}
            </Button>
          </div>

        </div>
      </main>
    </div>
  );
}
